import { IonContent, IonIcon, IonPage, IonSpinner } from '@ionic/react'
import { useQueryClient } from '@tanstack/react-query'
import { checkmark } from 'ionicons/icons'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LogoWithText from '../../components/LogoWithText'
import Button from '../../components/actions/Button'
import { ConsumptionDataContext } from '../../contexts/ConsumptionDataContext'
import useUser from '../../contexts/hooks/useUser'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'

interface LoadingPageProps {
	showBreakdown?: boolean
}

const LoadingPage: React.FC<LoadingPageProps> = ({ showBreakdown }) => {
	const queryClient = useQueryClient()

	const {
		consumptionLoading,
		invoiceLoading,
		currentMonthInvoiceAccumulation,
		lastAvailableConsumptionData,
	} = useContext(ConsumptionDataContext)
	const { id: objectId, useDatahub } = useContext(ObjectContext)

	const { user } = useUser()

	const { t } = useTranslation('pages_loadingPage')

	const [timedOut, setTimedOut] = useState(false)

	useEffect(() => {
		const timeOut = setTimeout(() => {
			setTimedOut(true)
		}, 20 * 1000)

		return () => {
			clearTimeout(timeOut)
		}
	}, [])

	const tryAgain = () => {
		queryClient.removeQueries('objectOptions' as any)
		queryClient.removeQueries('currentMonthInvoiceData' as any)
		queryClient.invalidateQueries('objectOptions' as any)
		queryClient.invalidateQueries('currentMonthInvoiceData' as any)
	}

	const breakdown = useMemo(() => {
		return [
			{
				label: t('kayttajatiedot'),
				ready: Boolean(user),
			},
			{
				label: t('kayttopaikkatiedot'),
				ready: Boolean(objectId),
			},
			{
				label: useDatahub ? `${t('kulutustiedot')}: Fingrid` : t('kulutustiedot'),
				ready: !consumptionLoading && Boolean(lastAvailableConsumptionData),
			},
			{
				label: t('laskukertyma'),
				ready: !invoiceLoading && Boolean(currentMonthInvoiceAccumulation) && !consumptionLoading,
			},
		]
	}, [user, objectId, invoiceLoading, consumptionLoading])

	return (
		<IonPage>
			<IonContent scrollEvents>
				<div id='loading-page' className='dark:bg-background-100-dark bg-background-200-light'>
					<LogoWithText size={108} />

					{showBreakdown ? (
						<div className='breakdown'>
							{breakdown.map(element => (
								<div className='row' key={element.label}>
									<div className='label text-text-dark dark:text-white'>{element.label}</div>
									<div className='status'>
										{element.ready ? (
											<IonIcon
												className='text-brand-secondary-100-light dark:text-brand-primary-100-dark'
												icon={checkmark}
											/>
										) : (
											<IonSpinner className='text-brand-secondary-100-light dark:text-brand-primary-100-dark' />
										)}
									</div>
								</div>
							))}
							{timedOut && showBreakdown && (
								<Button onClick={tryAgain}>{t('yritaUudelleen')}</Button>
							)}
						</div>
					) : (
						<IonSpinner className='mt-6 text-brand-secondary-100-light dark:text-brand-primary-100-dark' />
					)}
				</div>
			</IonContent>
		</IonPage>
	)
}

export default LoadingPage
